// public/js/features/files/pdf-thumb.js
// Page-1 thumbnails for uploaded PDFs, rendered in the browser with pdf.js.
//
// Why pdf.js is loaded here and not in index.html: it is 1.8 MB across library
// and worker, and the overwhelming majority of sessions never upload a PDF. So
// it is imported on first use only, and the module promise is cached so a batch
// of PDFs pays for it once.
//
// Why it is served from OUR origin (public/vendor/) rather than the S3 static
// bucket the other vendor libraries use: pdf.js v6 is ESM-only and runs its
// parser in a MODULE worker. A worker script must be same-origin — the browser
// refuses a cross-origin one outright — and the workaround pdf.js keeps for CDN
// setups (a blob wrapper that imports the absolute URL) needs CORS headers the
// static bucket does not send, on top of a bucket policy that grants access by
// `aws:Referer`, which a worker fetch does not carry. Same-origin sidesteps all
// of it. See docs/asset-files.md §6.
//
// Nothing here may throw: a PDF that cannot be rendered simply has no
// thumbnail and falls back to the type icon, exactly as before.

const PDFJS_VERSION = '6.3.289';

// Resolved against <base href>, so a deployment under a sub-path still works.
const vendorUrl = (file) => new URL(`vendor/${file}`, document.baseURI).href;

let _pdfjs = null;

/** Load pdf.js once, or null when it cannot be loaded. */
async function loadPdfJs() {
  if (_pdfjs) return _pdfjs;
  _pdfjs = (async () => {
    const lib = await import(/* webpackIgnore: true */ vendorUrl(`pdfjs-${PDFJS_VERSION}.min.mjs`));
    lib.GlobalWorkerOptions.workerSrc = vendorUrl(`pdfjs-${PDFJS_VERSION}.worker.min.mjs`);
    return lib;
  })().catch((e) => {
    console.warn('[files] pdf.js could not be loaded:', e?.message);
    _pdfjs = null; // let a later upload try again
    return null;
  });
  return _pdfjs;
}

/**
 * Render page 1 of a PDF to a JPEG blob.
 * @param {Blob} file      the PDF bytes
 * @param {number} maxEdge longest edge of the result, in pixels
 * @returns {Promise<Blob|null>} null when the PDF cannot be rendered
 */
export async function renderPdfThumb(file, maxEdge = 320) {
  let doc = null;
  try {
    const pdfjs = await loadPdfJs();
    if (!pdfjs) return null;

    const data = new Uint8Array(await file.arrayBuffer());
    // Only page 1 is ever needed, so keep pdf.js from pulling the whole file
    // apart, and stay quiet about the fonts/XFA a thumbnail does not care about.
    doc = await pdfjs.getDocument({
      data,
      disableAutoFetch: true,
      disableStream: true,
      isEvalSupported: false,
      verbosity: 0,
    }).promise;

    const page = await doc.getPage(1);
    const unscaled = page.getViewport({ scale: 1 });
    const scale = maxEdge / Math.max(unscaled.width, unscaled.height, 1);
    const viewport = page.getViewport({ scale: Math.min(scale, 4) });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const ctx = canvas.getContext('2d');
    // A PDF page is transparent where nothing is drawn, which would flatten to
    // black in a JPEG — paper is white.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // `canvas` and `canvasContext` name the same target across pdf.js versions;
    // passing both keeps this working either way.
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;

    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  } catch (e) {
    console.warn('[files] PDF thumbnail failed:', e?.message);
    return null;
  } finally {
    try { await doc?.destroy(); } catch { /* nothing to clean up */ }
  }
}

export const isPdfType = (ct) => String(ct || '').toLowerCase().startsWith('application/pdf');
